import BaseAccordion from "@/components/Accordion/Accordion/BaseAccordion";
import BaseAccordionItem from "@/components/Accordion/AccordionItem/BaseAccordionItem";
import { AllocationPerfomanceTable } from "@/components/AllocationPerfomanceTable/AllocationPerfomanceTable";
import { AsOfDateLabel } from "@/components/AsOfDateLabel/AsOfDateLabel";
import { ContentBlock } from "@/components/ContentBlock/ContentBlock";
import { Disclaimer } from "@/components/Disclaimer/Disclaimer";
import DownloadAllocationModal from "@/components/DownloadAllocationModal/DownloadAllocationModal";
import type { DocumentData } from "@/components/DownloadAllocationModal/DownloadAllocationModal";
import { useInstanceConfig } from "@/services/InstanceConfigContext";
import { useHtmlifiedTranslations } from "@/services/useHtmlifiedTranslations";
import { htmlify, interpolatePlaceholders } from "@/services/utils";
import type { DataTable } from "@/types";
import type { FundDetailLayoutComponentProps } from "@/types/fundDetailLayout";
import { Flex, Text, useMultiStyleConfig, useDisclosure, Button, Icon } from "@chakra-ui/react";
import { FaDownload } from "react-icons/fa";

export interface EtfHoldingsProps {
	heading: string;
	asOfLabel: string;
	asOfDate: string;
	downloadButtonLabel: string;
	table: DataTable;
	pageSize: number;
	downloadAllocationModal: {
		stages: {
			one: {
				title: string;
			},
			two: {
				title: string;
				downloadButtonLabel: string;
			}
		}
		documentData: DocumentData
	}
	disclaimer: string;
};

export const FxEtfHoldingsPanel = ({ dashboard }: FundDetailLayoutComponentProps) => {
	const { fxEtfHoldingsPanel: props } = dashboard;
	const { heading, table, asOfDate, asOfLabel, downloadAllocationModal, downloadButtonLabel, disclaimer, pageSize } = props;
	const styleParts = useMultiStyleConfig("ListingTable");
	const instanceConfig = useInstanceConfig();
	const { isOpen: isDownloadAllocationModalOpen, onOpen: onDownloadAllocationModalOpen, onClose: onDownloadAllocationModalClose } = useDisclosure();
	const { translations: encodedTranslations } = dashboard;
	const translations = useHtmlifiedTranslations(encodedTranslations);

	if (!props) {
		return null;
	}

	return (
		<>
			<DownloadAllocationModal
				{...downloadAllocationModal}
				isOpen={isDownloadAllocationModalOpen}
				onClose={onDownloadAllocationModalClose}
				translations={translations}
			/>
			<ContentBlock width="full" id="fxEtfHoldingsPanel" data-testid="fxEtfHoldingsPanel" sx={styleParts.root}>
				<BaseAccordion allowMultiple={true} variant="dividendInfoDrawer" defaultIndex={[0]}>
					<BaseAccordionItem title={heading}>
						<Flex flexDirection="column" gap={5}>
							<Flex direction="row" alignItems="center" pt={5}>
								<Flex flex={1}>
									<AsOfDateLabel label={asOfLabel} date={asOfDate} />
								</Flex>
								<Button variant="brand" aria-haspopup="dialog" onClick={onDownloadAllocationModalOpen}>
									<Icon as={FaDownload} />
									<Text marginLeft={2} sx={{ display: { base: "none", sm: "block" } }}>
										{downloadButtonLabel}
									</Text>
								</Button>
							</Flex>

							<AllocationPerfomanceTable
								data={table}
								pageSize={pageSize}
								translations={translations}
							/>

							{Boolean(disclaimer) && <Disclaimer>{htmlify(interpolatePlaceholders({ text: disclaimer, instanceConfig }))}</Disclaimer>}
						</Flex>
					</BaseAccordionItem>
				</BaseAccordion>
			</ContentBlock>
		</>
	);
};
